import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  FormControl,
  Box,
  Select,
  Typography,
  Button,
  Menu,
  MenuItem,
  TablePagination,
  Avatar,
  TextField,
  Grid,
} from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import RestoreIcon from "@mui/icons-material/Restore";
import { styled } from "@mui/system";

import Colors from "../../../assets/styles";
import { Svgs } from "../../../assets/images";
import { useNavigate } from "react-router-dom";
import PropertyServices from "../../../api/PropertyServices/property.index";
import { ErrorHandler } from "../../../utils/ErrorHandler";
import SimpleDialog from "../../../components/Dialog/index";
import Loader from "../../../components/Loader";
import AuthServices from "../../../api/AuthServices/auth.index";
import { AuthRoutes } from "../../../api/AuthServices/auth.route";
import AgentActivityChart from "./AgentActivityChart";

const tableHead = [
  "SR No",
  "Agent Name",
  "Email",
  "Address",
  "Phone Number",
  "Logins",
  "Website Visits",
  "Viewings",
  "Sold",
  "Feedbacks",
  // "Action",
];

const CustomSelect = styled(Select)({
  "&.MuiSelect-root": {
    border: "none",
    outline: "none",
    boxShadow: "none",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "& .MuiSelect-icon": {
    color: "black",
  },
  "& .MuiSelect-select": {
    padding: "8px 32px 8px 8px",
  },
});

const AgentReport = () => {
  const [data, setData] = useState([]);
  const [id, setId] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // filters
  const today = new Date().toISOString().split("T")[0];
  const [activityType, setActivityType] = useState("all");
  const [timeFilter, setTimeFilter] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const exportOpen = Boolean(exportAnchorEl);

  const baseUrl = process.env.REACT_APP_BASE_URL;
  const sleep = () => new Promise((r) => setTimeout(r, 1000));

  const getAgentList = async (
    searchParam = "",
    pageParam = 1,
    limitParam = 10,
    statusParam = "approved",
    activityTypeParam = "all",
    timeParam = "all",
    from = "",
    to = ""
  ) => {
    setLoading(true);
    try {
      await sleep();
      const { data } = await AuthServices.agentFilterList(
        searchParam,
        pageParam,
        limitParam,
        "approved",
        activityTypeParam,
        timeParam,
        from,
        to
      );
      setData(data?.agents);
      setCount(data.count);
    } catch (error) {
      ErrorHandler(error);
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAgentList(search, page + 1, limit, "approved", activityType, timeFilter, fromDate, toDate);
  }, [page, limit, search]);

  const handleSearch = () => {
    if (timeFilter === "custom" && (!fromDate || !toDate)) {
      alert("Please select both From and To dates for custom time filter.");
      return;
    }
    getAgentList(search, 1, limit, "approved", activityType, timeFilter, fromDate, toDate);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleReset = () => {
    setId("");
    setSearch("");
    setActivityType("all");
    setTimeFilter("all");
    setFromDate("");
    setToDate("");
    getAgentList("", 1, limit, "approved", "all", "all", "", "");
  };

  const filteredData = data.filter((item) =>
    item.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleExport = (type) => {
    const params = new URLSearchParams({
      search: search || "",
      status: "approved",
      activityType: activityType || "all",
      timeFilter: timeFilter || "all",
      fromDate: fromDate || "",
      toDate: toDate || "",
    });

    const url = `${process.env.REACT_APP_BASE_URL}${AuthRoutes.agentReportExport}?${params.toString()}&format=${type}`;
    window.open(url, "_blank");
  };

  return (
    <Box sx={{ padding: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 8,
        }}
      >
        {/* <Typography
          sx={{ fontSize: "26px", color: Colors.primary, fontWeight: "600" }}
        >
          Agents Report
        </Typography> */}
      </Box>
      <Box
        sx={{
          mt: 1,
          p: 1,
          borderRadius: 3,
          bgcolor: "#ffffff",
          border: "1px solid #e3e3e3",
          boxShadow: "0 3px 8px rgba(0,0,0,0.06)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <CustomSelect
            value={activityType}
            onChange={(e) => setActivityType(e.target.value)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="all">All Activities</MenuItem>
            <MenuItem value="login">Logins</MenuItem>
            <MenuItem value="website_visit">Website Visits</MenuItem>
            <MenuItem value="property_viewing_booked">Viewings</MenuItem>
            <MenuItem value="property_sold">Sold</MenuItem>
          </CustomSelect>

          <CustomSelect
            value={timeFilter}
            onChange={(e) => setTimeFilter(e.target.value)}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="all">All Time</MenuItem>
            <MenuItem value="weekly">Weekly</MenuItem>
            <MenuItem value="monthly">Monthly</MenuItem>
            <MenuItem value="yearly">Yearly</MenuItem>
            <MenuItem value="custom">Custom</MenuItem>
          </CustomSelect>

          {timeFilter === "custom" && (
            <>
              <TextField
                type="date"
                label="From"
                InputLabelProps={{ shrink: true }}
                value={fromDate}
                inputProps={{ max: today }}
                onChange={(e) => setFromDate(e.target.value)}
              />

              <TextField
                type="date"
                label="To"
                InputLabelProps={{ shrink: true }}
                value={toDate}
                inputProps={{ max: today }}
                onChange={(e) => setToDate(e.target.value)}
              />
            </>
          )}

          <TextField
            placeholder="Search Agent"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 200 }}
          />

          <Button
            variant="contained"
            sx={{
              bgcolor: Colors.primary,
              color: "#fff",
              px: 3,
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
            onClick={handleSearch}
          >
            Apply Filters
          </Button>

          <Button color="error" onClick={handleReset}>
            Reset
          </Button>
        </Box>
      </Box>

      {loading ? (
        <TableRow>
          <TableCell colSpan={tableHead.length}>
            <Loader width="40px" height="40px" color={Colors.primary} />
          </TableCell>
        </TableRow>
      ) : (
        <AgentActivityChart data={filteredData} />
      )}

      <TableContainer
        sx={{
          mt: 2,
          backgroundColor: Colors.backgroundColor,
          borderRadius: "10px",
          display:"none"
        }}
        component={Paper}
      >
        <Table>
          <TableHead>
            <TableRow>
              {tableHead.map((column, index) => (
                <TableCell
                  key={index}
                  sx={{
                    fontWeight: "600",
                    color: Colors.black,
                    textAlign:
                      column === "Management Company" ? "left" : "center",
                  }}
                >
                  {column}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          {loading ? (
            <TableRow>
              <TableCell colSpan={tableHead.length}>
                <Loader width="40px" height="40px" color={Colors.primary} />
              </TableCell>
            </TableRow>
          ) : (
            filteredData.map((row, index) => (
              <TableBody key={row._id}>
                <TableRow
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/agent/detail/${row._id}`);
                  }}
                >
                  <TableCell sx={{ textAlign: "center" }}>
                    {index + 1}
                  </TableCell>

                  <TableCell sx={{ textAlign: "center" }}>
                    {row?.name}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center" }}>
                    {row?.email}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center" }}>
                    {row?.address}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center" }}>
                    {row?.phone}
                  </TableCell>

                  {/* filter fields */}
                  <TableCell sx={{ textAlign: "center" }}>
                    {row?.loginCount}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center" }}>
                    {row?.visitCount}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center" }}>
                    {row?.viewingCount}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center" }}>
                    {row?.soldCount}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center" }}>
                    {row?.FeedbackCount}
                  </TableCell>
                </TableRow>
              </TableBody>
            ))
          )
          }
        </Table>
        {filteredData.length > 0 && (
          <>
            <Button
              variant="contained"
              sx={{
                bgcolor: Colors.primary,
                color: "#fff",
                px: 3,
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
              onClick={(e) => setExportAnchorEl(e.currentTarget)}
            >
              Export Report
            </Button>
            <Menu
              anchorEl={exportAnchorEl}
              open={exportOpen}
              onClose={() => setExportAnchorEl(null)}
            >
              <MenuItem
                onClick={() => {
                  setExportAnchorEl(null);
                  handleExport("csv");
                }}
              >
                Export as CSV
              </MenuItem>

              <MenuItem
                onClick={() => {
                  setExportAnchorEl(null);
                  handleExport("pdf");
                }}
              >
                Export as PDF
              </MenuItem>
            </Menu>
          </>
        )}
      </TableContainer>
      <Box
        sx={{
          mt: 2,
          display: "none",
          // display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography sx={{ color: "text.secondary", ml: 2 }}>
          {`Showing ${page * limit + 1}-${Math.min(
            (page + 1) * limit,
            count
          )} of ${count}`}
        </Typography>
        <Box>
          <TablePagination
            component={Paper}
            sx={{
              borderBottom: "none",
              border: "1px solid black",
              bgcolor: "transparent",
              ".MuiTablePagination-toolbar": {
                paddingLeft: 0,
              },
              ".MuiTablePagination-spacer": {
                flex: "none",
              },
              ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows":
              {
                display: "none",
              },
              ".MuiTablePagination-actions": {
                marginLeft: 0,
              },
              ".MuiTablePagination-actions button:first-child": {
                borderRadius: 0,
                p: 0,
                pr: "8px",
                borderRight: "1px solid black",
              },
              ".css-78c6dr-MuiToolbar-root-MuiTablePagination-toolbar .MuiTablePagination-actions":
              {
                marginLeft: "14px !important",
              },
              ".MuiTablePagination-toolbar": {
                minHeight: "14px !important",
                padding: "0px !important",
              },
            }}
            rowsPerPageOptions={[]} // Removes rows per page selector
            count={count}
            rowsPerPage={limit}
            page={page}
            onPageChange={handlePageChange}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default AgentReport;
